import { createTheme } from "@mui/material/styles";
import * as tokens from "@shape-ds/tokens";
import { baseTheme } from "./base";
import { tooltipClasses } from "@mui/material";

const materialTheme = createTheme(
  {
    palette: {
      mode: "light",
      primary: {
        main: tokens.lightColors.BLUE_2,
        dark: tokens.lightColors.BLUE_3,
      },
      error: { main: tokens.lightColors.RED_1 },
      success: { main: tokens.lightColors.GREEN_1 },
      warning: { main: tokens.lightColors.YELLOW_1 },
    },
    components: {
      MuiAlert: {
        variants: [
          { props: { severity: "success" }, style: { background: tokens.lightColors.GREEN_1 } },
          { props: { severity: "error" }, style: { background: tokens.lightColors.RED_1 } },
          { props: { severity: "info" }, style: { background: tokens.lightColors.BLUE_1 } },
          {
            props: { severity: "warning" },
            style: { background: tokens.lightColors.YELLOW_1, color: tokens.lightColors.GRAY_1 },
          },
        ],
        styleOverrides: {
          root: {
            color: tokens.lightColors.GRAY_1,
          },
        },
      },
      MuiButton: {
        defaultProps: { sx: { fontWeight: 600, minWidth: "120px" } },
        variants: [
          {
            props: { variant: "contained" },
            style: {
              color: tokens.lightColors.GRAY_1,
              backgroundColor: tokens.lightColors.BLUE_2,
              fontFamily: tokens.TYPOGRAPHY_FAMILY_ROBOTO,
              fontWeight: tokens.TYPOGRAPHY_FONT_WEIGHT_BOLD,
              "&:hover": { backgroundColor: tokens.lightColors.BLUE_3 },
              "&:disabled": {
                color: tokens.lightColors.GRAY_1,
                backgroundColor: tokens.lightColors.GRAY_4,
              },
            },
          },
          {
            props: { variant: "outlined" },
            style: {
              color: tokens.lightColors.BLUE_2,
              borderColor: tokens.lightColors.BLUE_2,
              backgroundColor: "unset",
              "&:hover": {
                color: tokens.lightColors.BLUE_3,
                borderColor: tokens.lightColors.BLUE_3,
                backgroundColor: "unset",
              },
              "&:disabled": {
                color: tokens.lightColors.GRAY_5,
                borderColor: tokens.lightColors.GRAY_4,
                backgroundColor: "unset",
                svg: { fill: `${tokens.darkColors.GRAY_5} !important` },
              },
            },
          },
          {
            props: { variant: "text" },
            style: {
              color: tokens.lightColors.BLUE_2,
              backgroundColor: "transparent",
              "&:hover": {
                color: tokens.lightColors.BLUE_3,
                backgroundColor: "unset",
              },
              "&:disabled": {
                color: tokens.lightColors.GRAY_4,
                backgroundColor: "unset",
              },
            },
          },
        ],
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.lightColors.GRAY_1,
          },
        },
      },
      MuiDialog: {
        defaultProps: {
          PaperProps: {
            sx: {
              background: tokens.lightColors.GRAY_1,
            },
          },
        },
      },
      MuiDialogContent: {
        defaultProps: {
          sx: {
            color: tokens.lightColors.GRAY_7,
          },
        },
      },
      MuiDialogTitle: {
        defaultProps: {
          sx: {
            color: tokens.lightColors.GRAY_7,
            "& .MuiIconButton-root": { color: tokens.lightColors.GRAY_5 },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            color: tokens.lightColors.GRAY_7,
          },
        },
      },
      MuiFormLabel: {
        defaultProps: {
          sx: { "&.Mui-focused": { color: tokens.lightColors.GRAY_7 } },
        },
        styleOverrides: {
          root: {
            color: tokens.lightColors.GRAY_7,
          },
        },
      },
      MuiInputBase: {
        defaultProps: {
          sx: {
            "&.Mui-focused": { border: `1px solid ${tokens.lightColors.BLUE_2}` },
            border: `1px solid ${tokens.lightColors.GRAY_5}`,
          },
        },
        variants: [
          {
            props: { error: true },
            style: { borderColor: `${tokens.lightColors.RED_1} !important` },
          },
          {
            props: { disabled: true },
            style: { background: tokens.lightColors.GRAY_4 },
          },
        ],
      },
      MuiRadio: {
        defaultProps: {
          sx: {
            color: tokens.lightColors.GRAY_7,
            "&.Mui-disabled": { color: tokens.lightColors.GRAY_4 },
            "&.Mui-checked": { color: tokens.lightColors.BLUE_2 },
          },
        },
      },
      MuiTabs: {
        defaultProps: {
          sx: {
            "& .MuiTab-root": { color: tokens.lightColors.GRAY_6 },
            "& .MuiTabs-indicator": { background: tokens.lightColors.GRAY_6 },
            "& .Mui-selected": {
              color: `${tokens.lightColors.GRAY_7} !important`,
            },
            "& .Mui-disabled": {
              color: `${tokens.lightColors.GRAY_4} !important`,
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiButtonBase-root": { color: tokens.lightColors.GRAY_6 },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: tokens.lightColors.GRAY_1,
            color: tokens.lightColors.GRAY_7,
            boxShadow: `0px 2px 8px 0px ${tokens.lightColors.GRAY_7}`,
            [`& .${tooltipClasses.arrow}`]: {
              color: tokens.lightColors.GRAY_1,
            },
          },
        },
      },
    },
    shapeTheme: "light",
    tokens: {
      colors: {
        blue: {
          "1": tokens.lightColors.BLUE_1,
          "2": tokens.lightColors.BLUE_2,
          "3": tokens.lightColors.BLUE_3,
          "4": tokens.lightColors.BLUE_4,
        },
        gray: {
          "1": tokens.lightColors.GRAY_1,
          "2": tokens.lightColors.GRAY_2,
          "3": tokens.lightColors.GRAY_3,
          "4": tokens.lightColors.GRAY_4,
          "5": tokens.lightColors.GRAY_5,
          "6": tokens.lightColors.GRAY_6,
          "7": tokens.lightColors.GRAY_7,
        },
        green: {
          "1": tokens.lightColors.GREEN_1,
          "2": tokens.lightColors.GREEN_2,
          "3": tokens.lightColors.GREEN_3,
        },
        pink: {
          "1": tokens.lightColors.PINK_1,
          "2": tokens.lightColors.PINK_2,
        },
        red: {
          "1": tokens.lightColors.RED_1,
          "2": tokens.lightColors.RED_2,
          "3": tokens.lightColors.RED_3,
        },
        yellow: {
          "1": tokens.lightColors.YELLOW_1,
          "2": tokens.lightColors.YELLOW_2,
        },
      },
    },
  },
  baseTheme
);

export const lightTheme = materialTheme;
